import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { ScoreInfo } from "@/components/mai/Explain";
import { useProduct } from "@/context/ProductContext";
import { useAuth } from "@/context/AuthContext";
import { PrioritizationUncertainty } from "@/components/mai/PrioritizationUncertainty";
import { ChartLineUp, CaretDown, CaretUp, CircleNotch, ArrowsClockwise, Lightning, Sparkle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIMS = [
  ["commercial_potential", "Commercial", false],
  ["market_attractiveness", "Attractiveness", false],
  ["evidence_readiness", "Evidence", false],
  ["access_complexity", "Complexity", true],
  ["competitive_intensity", "Competition", true],
  ["implementation_effort", "Effort", true],
];
const PORTFOLIO_QS = [
  { q: "efficient_frontier", label: "Highest revenue, lowest evidence investment" },
  { q: "current_evidence", label: "Accessible with current evidence" },
  { q: "rwe_uplift", label: "What if we add one RWE study?" },
];
const dimColor = (v, inverted) => {
  const good = inverted ? 100 - v : v;
  return good >= 70 ? "#047857" : good >= 45 ? "#B45309" : "#B91C1C";
};

const DimBar = ({ label, value, inverted }) => (
  <div className="w-[76px]">
    <p className="text-[9px] uppercase tracking-wider text-slate-400 truncate">{label}</p>
    <div className="flex items-center gap-1.5 mt-0.5">
      <div className="flex-1 h-1.5 bg-slate-100"><div className="h-full" style={{ width: `${value}%`, background: dimColor(value, inverted) }} /></div>
      <span className="font-mono-data text-[10px] font-bold text-slate-600 w-5 text-right">{value}</span>
    </div>
  </div>
);

function MarketRow({ r }) {
  const [open, setOpen] = useState(r.rank === 1);
  return (
    <div data-testid={`priority-row-${r.id}`} className="bg-white border border-slate-200">
      <button onClick={() => setOpen(!open)} className="w-full px-5 py-3.5 flex items-center gap-4 text-left hover:bg-slate-50/60">
        <span className="font-heading font-extrabold text-lg text-slate-300 w-8 shrink-0">#{r.rank}</span>
        <div className="w-52 shrink-0 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{r.flag} {r.name}</p>
          <p className="text-[10px] text-slate-400">{r.verdict} · ~{r.time_to_market_months} mo to market</p>
        </div>
        <div className="hidden xl:flex items-center gap-3 flex-1">
          {DIMS.map(([k, label, inv]) => <DimBar key={k} label={label} value={r.scores[k]} inverted={inv} />)}
        </div>
        <div className="text-right shrink-0 w-20">
          <p className="overline-label text-slate-400">Priority</p>
          <p className="font-heading font-extrabold text-xl leading-none">{r.overall_priority}</p>
        </div>
        {open ? <CaretUp size={14} className="text-slate-400 shrink-0" /> : <CaretDown size={14} className="text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div data-testid={`priority-detail-${r.id}`} className="border-t border-slate-100 px-5 py-4 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4 bg-slate-50/40">
          <div className="lg:col-span-2 flex items-start gap-2">
            <Sparkle size={14} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-700 leading-relaxed"><span className="font-semibold">Why #{r.rank}:</span> {r.ai_recommendation}</p>
          </div>
          <div>
            <p className="overline-label text-slate-400">Recommended entry strategy</p>
            <p className="text-xs text-slate-600 leading-relaxed mt-1">{r.entry_strategy}</p>
            {r.key_challenges?.length > 0 && <>
              <p className="overline-label text-slate-400 mt-3">Expected key challenges</p>
              <ul className="mt-1 space-y-0.5">{r.key_challenges.map((c, i) => <li key={i} className="text-xs text-slate-600">• {c}</li>)}</ul>
            </>}
            {r.competitive_risks?.length > 0 && <>
              <p className="overline-label text-slate-400 mt-3">Competitive risks</p>
              <ul className="mt-1 space-y-0.5">{r.competitive_risks.map((c, i) => <li key={i} className="text-xs text-slate-600">• {c}</li>)}</ul>
            </>}
          </div>
          <div>
            <p className="overline-label text-slate-400">Evidence gap summary</p>
            <p className="text-xs text-slate-600 mt-1">
              {r.evidence_gaps.length ? r.evidence_gaps.join(" · ") : "No gating evidence gaps."}
              <span className="text-slate-400"> ({r.localization.missing} missing · {r.localization.partial} need adaptation of {r.localization.total_requirements})</span>
            </p>
            {r.next_actions?.length > 0 && <>
              <p className="overline-label text-slate-400 mt-3">Suggested next actions</p>
              <ul className="mt-1 space-y-0.5">{r.next_actions.map((a, i) => <li key={i} className="text-xs text-slate-600">→ {a}</li>)}</ul>
            </>}
            <p className="overline-label text-slate-400 mt-3">Score basis (AI-estimated commercial dimensions)</p>
            <ul className="mt-1 space-y-0.5 text-[11px] text-slate-500 leading-relaxed">
              <li><span className="font-semibold text-slate-600">Attractiveness:</span> {r.basis.attractiveness}</li>
              <li><span className="font-semibold text-slate-600">Competition:</span> {r.basis.competitive}</li>
              <li><span className="font-semibold text-slate-600">Commercial:</span> {r.basis.commercial}</li>
            </ul>
            {(r.assumptions?.length > 0 || r.uncertainties?.length > 0) && (
              <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
                {r.assumptions?.length > 0 && <>Assumptions: {r.assumptions.join("; ")}. </>}
                {r.uncertainties?.length > 0 && <>Uncertainties: {r.uncertainties.join("; ")}.</>}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PrioritizationPage() {
  const { productId, product } = useProduct();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [doc, setDoc] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioBusy, setPortfolioBusy] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(() => axios.get(`${API}/prioritization/${productId}`).then((r) => {
    setDoc(r.data);
    return r.data;
  }), [productId]);

  useEffect(() => {
    setDoc(null); setPortfolio(null);
    if (productId) load().catch(console.error);
    return () => clearInterval(pollRef.current);
  }, [productId, load]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (doc?.status === "processing") {
      pollRef.current = setInterval(() => load().catch(() => {}), 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [doc?.status, load]);

  const generate = async () => {
    try {
      const r = await axios.post(`${API}/prioritization/${productId}/generate`);
      setDoc(r.data);
      toast.success("Strategic analysis running — scoring all markets and generating recommendations");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start analysis");
    }
  };

  const askPortfolio = async (q) => {
    setPortfolioBusy(q);
    try {
      const r = await axios.get(`${API}/prioritization/${productId}/portfolio`, { params: { q } });
      setPortfolio(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Query failed");
    } finally {
      setPortfolioBusy(null);
    }
  };

  const res = doc?.status === "completed" ? doc.result : null;
  return (
    <PageShell pageId="prioritization" overline={`Strategic Decision Layer · ${product?.name || ""}`} title="Strategic Market Prioritization"
      actions={res && (
        <button data-testid="regenerate-btn" onClick={generate}
          className="text-xs font-semibold text-slate-900 border border-slate-300 px-4 py-2.5 hover:bg-slate-50 flex items-center gap-2">
          <ArrowsClockwise size={14} /> Re-run analysis
        </button>
      )}>
      {!doc ? (
        <div className="py-32 text-center text-slate-400 text-sm">Loading…</div>
      ) : doc.status === "none" || doc.status === "failed" ? (
        <div data-testid="prioritization-empty" className="bg-white border border-slate-200 py-16 text-center">
          <ChartLineUp size={32} className="text-slate-300 mx-auto" />
          <p className="font-heading font-bold text-base text-slate-800 mt-3">Where should {product?.name || "this product"} launch next — and why?</p>
          <p className="text-xs text-slate-400 mt-1 max-w-lg mx-auto">The engine scores every registry market across six strategic dimensions — commercial, competitive, evidence, complexity — and produces a ranked, explainable launch priority list.</p>
          {doc.status === "failed" && <p className="text-xs text-red-600 mt-3">Last run failed: {doc.error}</p>}
          <button data-testid="generate-btn" onClick={generate}
            className="mt-5 bg-slate-900 text-white text-xs font-semibold px-6 py-3 hover:bg-slate-800 inline-flex items-center gap-2">
            <Lightning size={14} weight="fill" /> Generate Strategic Prioritization
          </button>
        </div>
      ) : doc.status === "processing" ? (
        <div data-testid="prioritization-processing" className="bg-white border border-slate-200 py-20 text-center">
          <CircleNotch size={28} className="text-slate-400 mx-auto animate-spin" />
          <p className="font-heading font-bold text-sm text-slate-700 mt-3">AI is scoring every market…</p>
          <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">Computing deterministic readiness dimensions, estimating commercial context, ranking, and writing per-market strategy recommendations. Usually 1–2 minutes.</p>
        </div>
      ) : (
        <>
          <div data-testid="prioritization-summary" className="grid grid-cols-2 md:grid-cols-4 bg-white border border-slate-200 divide-x divide-slate-200">
            {[
              ["Markets Ranked", res.summary.markets_ranked],
              ["Top Priority Market", res.summary.top_market],
              ["Accessible Today", res.summary.accessible_now],
              ["Avg Time to Market", `${res.summary.avg_time_to_market} mo`],
            ].map(([label, value]) => (
              <div key={label} className="px-5 py-4">
                <p className="overline-label text-slate-400">{label}{label === "Markets Ranked" && <> <ScoreInfo scoreId="strategic_priority" /></>}</p>
                <p className="font-heading font-extrabold text-xl mt-1 truncate">{value}</p>
              </div>
            ))}
          </div>

          <section data-testid="portfolio-panel" className="bg-slate-900 text-white p-5">
            <p className="overline-label text-emerald-400">Portfolio View — Executive Questions</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {PORTFOLIO_QS.map((p) => (
                <button key={p.q} data-testid={`portfolio-q-${p.q}`} onClick={() => askPortfolio(p.q)} disabled={portfolioBusy}
                  className={`text-[11px] px-3 py-2 border transition-colors ${portfolio?.question && portfolioBusy === null && portfolio._q === p.q ? "border-emerald-500 text-white" : "border-white/20 text-slate-300 hover:border-emerald-500 hover:text-white"}`}>
                  {portfolioBusy === p.q ? <CircleNotch size={12} className="animate-spin inline" /> : null} {p.label}
                </button>
              ))}
            </div>
            {portfolio && (
              <div data-testid="portfolio-answer" className="mt-4 border-t border-white/10 pt-4">
                <p className="text-xs font-semibold">{portfolio.question}</p>
                <p className="text-xs text-slate-300 leading-relaxed mt-1">{portfolio.answer}</p>
                {portfolio.markets?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {portfolio.markets.map((m) => (
                      <span key={m.id} className="text-[11px] border border-white/15 px-2.5 py-1.5">
                        {m.flag} {m.name} <span className="text-emerald-400 font-mono-data">{m.status || m.readiness}</span>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-white/40 mt-3">{portfolio.how}</p>
              </div>
            )}
          </section>

          <section className="space-y-2">
            <div className="flex items-baseline justify-between">
              <p className="font-heading font-bold text-sm tracking-tight">Priority Markets — Ranked</p>
              <p className="text-[10px] text-slate-400">Generated {doc.completed_at ? new Date(doc.completed_at).toLocaleString() : ""} · click any market for full rationale</p>
            </div>
            {res.ranking.map((r) => <MarketRow key={r.id} r={r} />)}
          </section>

          <PrioritizationUncertainty productId={productId} isAdmin={isAdmin} />

          <section data-testid="methodology-panel" className="bg-white border border-slate-200 px-5 py-4">
            <p className="overline-label text-slate-400">Methodology — how this ranking was produced</p>
            <div className="mt-2 space-y-2">
              {res.methodology.map((m, i) => (
                <p key={i} className="text-xs text-slate-600 leading-relaxed"><span className="font-semibold text-slate-800">{m.step}:</span> {m.detail}</p>
              ))}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
